import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money, errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Bed, Presentation, ForkKnife, Plus, PencilSimple, Trash, ClockCounterClockwise } from "@phosphor-icons/react";

const KIND_META = {
  rooms: { label: "Guest rooms", Icon: Bed, color: "#002FA7", bg: "#E0E7FF" },
  meeting: { label: "Meeting / function", Icon: Presentation, color: "#B45309", bg: "#FEF3C7" },
  fnb: { label: "Food & beverage", Icon: ForkKnife, color: "#047857", bg: "#D1FAE5" },
};
const FUNCTION_TYPES = ["Breakfast", "AM Break", "Lunch", "PM Break", "Dinner", "Reception", "General Session", "Meeting", "Other"];
const inputCls = "w-full border border-[#E5E7EB] px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";

function nightsBetween(start, end) {
  if (!start) return [];
  const s = new Date(start + "T00:00:00");
  const e = end ? new Date(end + "T00:00:00") : new Date(s);
  const out = [];
  const cur = new Date(s);
  // nights = arrival .. night before departure
  while (cur < e || (out.length === 0 && cur <= e && !end)) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
    if (out.length > 60) break;
  }
  if (out.length === 0) out.push(start);
  return out;
}
const fmtDay = (d) => { const dt = new Date(d + "T00:00:00"); return `${dt.toLocaleDateString("en-US", { weekday: "short" })} ${dt.getMonth() + 1}/${dt.getDate()}`; };

function Field({ label, children }) {
  return <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">{label}</span>{children}</label>;
}

export default function SpaceEntries({ leadId, hotelId, entries = [] }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editKind, setEditKind] = useState(null);   // kind being edited (edit mode only)
  const [showHistory, setShowHistory] = useState(null);

  const { data: roomTypes = [] } = useQuery({ queryKey: ["room-types", hotelId], queryFn: () => api.roomTypes(hotelId), enabled: !!hotelId });
  const { data: meetingRooms = [] } = useQuery({ queryKey: ["meeting-rooms", hotelId], queryFn: () => api.meetingRooms(hotelId), enabled: !!hotelId });

  // shared
  const [entryType, setEntryType] = useState("agreement");
  const [activeTab, setActiveTab] = useState("rooms");
  const [notes, setNotes] = useState("");
  // rooms section
  const [roomDates, setRoomDates] = useState({ start_date: "", end_date: "" });
  const [grid, setGrid] = useState({});   // { roomType: { date: qty } }
  const [rates, setRates] = useState({});  // { roomType: rate }
  // meeting section
  const [mtg, setMtg] = useState({});
  // fnb section
  const [fnb, setFnb] = useState({});

  const nights = useMemo(() => nightsBetween(roomDates.start_date, roomDates.end_date), [roomDates.start_date, roomDates.end_date]);
  const Sm = (k) => (e) => setMtg((s) => ({ ...s, [k]: e.target.value }));
  const Sf = (k) => (e) => setFnb((s) => ({ ...s, [k]: e.target.value }));
  const setQty = (rt, date, val) => setGrid((g) => ({ ...g, [rt]: { ...(g[rt] || {}), [date]: val } }));

  const resetForm = () => {
    setEntryType("agreement"); setNotes("");
    setRoomDates({ start_date: "", end_date: "" }); setGrid({}); setRates({});
    setMtg({}); setFnb({});
  };

  const openAdd = () => {
    setEditId(null); setEditKind(null); setActiveTab("rooms");
    resetForm(); setOpen(true);
  };

  const openEdit = (e) => {
    resetForm();
    setEditId(e.id); setEditKind(e.kind || "rooms"); setActiveTab(e.kind || "rooms");
    setEntryType(e.entry_type || "agreement"); setNotes(e.notes || "");
    if (e.kind === "rooms") {
      setRoomDates({ start_date: e.start_date || "", end_date: e.end_date || "" });
      const g = {}, r = {};
      (e.rows || []).forEach((row) => { g[row.room_type] = { ...(row.quantities || {}) }; r[row.room_type] = row.rate ?? ""; });
      setGrid(g); setRates(r);
    } else if (e.kind === "meeting") {
      setMtg({
        room_id: e.room_id || "", function_type: e.function_type || "", event_date: e.event_date || "",
        start_time: e.start_time || "", end_time: e.end_time || "", set_for: e.set_for || "",
        guarantee: e.guarantee || "", setup: e.setup || "", space_rate: e.space_rate || "",
        fnb_minimum: e.fnb_minimum || "", fnb_notes: e.fnb_notes || "",
      });
    } else {
      setFnb({ fnb_minimum: e.fnb_minimum || "", fnb_notes: e.fnb_notes || "" });
    }
    setOpen(true);
  };

  const invalidate = () => qc.invalidateQueries({ queryKey: ["lead", leadId] });

  // ---- section builders (return null when section is empty) ----
  const buildRooms = () => {
    const rows = roomTypes.map((rt) => ({
      room_type: rt.name,
      rate: Number(rates[rt.name]) || 0,
      quantities: Object.fromEntries(Object.entries(grid[rt.name] || {}).filter(([, q]) => Number(q) > 0).map(([d, q]) => [d, Number(q)])),
    })).filter((row) => Object.keys(row.quantities).length > 0);
    if (!rows.length) return null;
    return { kind: "rooms", entry_type: entryType, notes, start_date: roomDates.start_date || "", end_date: roomDates.end_date || "", rows };
  };
  const hasMeeting = !!(mtg.room_id || mtg.function_type || mtg.event_date || Number(mtg.space_rate) > 0 || Number(mtg.set_for) > 0 || Number(mtg.fnb_minimum) > 0);
  const buildMeeting = () => {
    if (!hasMeeting) return null;
    return {
      kind: "meeting", entry_type: entryType, notes, room_id: mtg.room_id || "", function_type: mtg.function_type || "",
      event_date: mtg.event_date || "", start_time: mtg.start_time || "", end_time: mtg.end_time || "",
      set_for: Number(mtg.set_for) || 0, guarantee: Number(mtg.guarantee) || 0, setup: mtg.setup || "",
      space_rate: Number(mtg.space_rate) || 0, fnb_minimum: Number(mtg.fnb_minimum) || 0, fnb_notes: mtg.fnb_notes || "",
    };
  };
  const hasFnb = !!(Number(fnb.fnb_minimum) > 0 || fnb.fnb_notes);
  const buildFnb = () => {
    if (!hasFnb) return null;
    return { kind: "fnb", entry_type: entryType, notes, fnb_minimum: Number(fnb.fnb_minimum) || 0, fnb_notes: fnb.fnb_notes || "" };
  };

  const roomsRow = buildRooms();
  const sections = useMemo(() => {
    const s = { rooms: !!roomsRow, meeting: hasMeeting, fnb: hasFnb };
    return s;
    // eslint-disable-next-line
  }, [grid, rates, roomDates, mtg, fnb]);

  const summaryParts = [];
  if (sections.rooms) summaryParts.push("1 room block");
  if (sections.meeting) summaryParts.push("1 meeting / function");
  if (sections.fnb) summaryParts.push("1 F&B minimum");

  const save = useMutation({
    mutationFn: () => {
      if (editId) {
        const body = editKind === "rooms" ? buildRooms() : editKind === "meeting" ? buildMeeting() : buildFnb();
        return api.updateSpaceEntry(leadId, editId, body || { kind: editKind, entry_type: entryType, notes });
      }
      const bodies = [buildRooms(), buildMeeting(), buildFnb()].filter(Boolean);
      return api.addSpaceEntriesBulk(leadId, bodies);
    },
    onSuccess: () => {
      invalidate();
      toast.success(editId ? "Updated" : `Added ${summaryParts.length} item${summaryParts.length > 1 ? "s" : ""}`);
      setOpen(false);
    },
    onError: (e) => toast.error(errMsg(e, "Could not save")),
  });

  const onSaveClick = () => {
    if (!editId && summaryParts.length === 0) {
      toast.error("Enter at least one of guest rooms, meeting or F&B before saving");
      return;
    }
    save.mutate();
  };

  const remove = useMutation({
    mutationFn: (eid) => api.deleteSpaceEntry(leadId, eid),
    onSuccess: () => { invalidate(); toast.success("Removed"); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const totals = entries.reduce((a, e) => {
    a.revenue += Number(e.revenue) || 0; a.room_nights += Number(e.room_nights) || 0;
    if (e.entry_type === "agreement") a.agr += Number(e.revenue) || 0; else a.act += Number(e.revenue) || 0;
    return a;
  }, { revenue: 0, room_nights: 0, agr: 0, act: 0 });

  const tabs = editId ? [editKind] : ["rooms", "meeting", "fnb"];

  return (
    <div className="border border-[#111827] p-6" data-testid="space-entries">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Bed size={18} weight="bold" color="#002FA7" /><span className="font-semibold text-[#111827]">Rooms, Space &amp; F&amp;B</span></div>
        <button data-testid="space-add-btn" onClick={openAdd} className="inline-flex items-center gap-1.5 bg-[#002FA7] text-white px-3 py-1.5 text-sm font-semibold hover:bg-[#00227A]"><Plus size={15} weight="bold" /> Add</button>
      </div>

      {entries.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-4" data-testid="space-rollup">
          <div className="bg-[#F3F4F6] px-3 py-2"><div className="overline text-[#4B5563]">Room-nights</div><div className="font-display font-black text-xl text-[#111827]">{totals.room_nights}</div></div>
          <div className="bg-[#E0E7FF] px-3 py-2"><div className="overline text-[#4B5563]">Agreement $</div><div className="font-display font-black text-xl text-[#002FA7]">{money(totals.agr)}</div></div>
          <div className="bg-[#D1FAE5] px-3 py-2"><div className="overline text-[#4B5563]">Actual $</div><div className="font-display font-black text-xl text-[#047857]">{money(totals.act)}</div></div>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {entries.length === 0 && <p className="text-sm text-[#4B5563]" data-testid="space-empty">No rooms, meeting space or F&amp;B tracked yet.</p>}
        {entries.map((e) => {
          const meta = KIND_META[e.kind] || KIND_META.rooms; const Icon = meta.Icon;
          return (
            <div key={e.id} data-testid={`space-entry-${e.id}`} className="border border-[#E5E7EB] p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 min-w-0">
                  <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 shrink-0" style={{ background: meta.bg }}><Icon size={16} weight="bold" color={meta.color} /></span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#111827] text-sm">{meta.label}</span>
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 ${e.entry_type === "agreement" ? "bg-[#E0E7FF] text-[#002FA7]" : "bg-[#D1FAE5] text-[#047857]"}`}>{e.entry_type}</span>
                    </div>
                    <div className="text-sm text-[#4B5563] mt-1">
                      {e.kind === "rooms" && (e.rows?.length
                        ? <>{e.start_date}{e.end_date ? `–${e.end_date}` : ""} · {e.room_nights} room-nights · {(e.rows || []).map((r) => r.room_type).join(", ")}</>
                        : <>{e.num_rooms || 0} × {e.room_type || "rooms"} · {money(e.nightly_rate)}/nt · {e.nights || 0} nights</>)}
                      {e.kind === "meeting" && <>{e.room_name || "Room"} · {e.function_type || "Function"} · {e.event_date} {e.start_time}{e.end_time ? `–${e.end_time}` : ""} · set {e.set_for || 0}{e.guarantee ? ` / gtd ${e.guarantee}` : ""}{e.setup ? ` · ${e.setup}` : ""}{e.fnb_minimum ? ` · F&B ${money(e.fnb_minimum)}` : ""}</>}
                      {e.kind === "fnb" && <>Minimum {money(e.fnb_minimum)}{e.fnb_notes ? ` · ${e.fnb_notes}` : ""}</>}
                    </div>
                    {e.kind === "rooms" && e.rows?.length > 0 && (
                      <div className="mt-2 overflow-x-auto">
                        <table className="text-xs border-collapse">
                          <thead><tr><th className="text-left pr-3 py-0.5 text-[#4B5563]">Room type</th>{(e.rows[0]?.quantities ? Object.keys(Object.assign({}, ...e.rows.map((r) => r.quantities))).sort() : []).map((d) => <th key={d} className="px-2 py-0.5 text-[#4B5563]">{fmtDay(d)}</th>)}<th className="px-2 text-[#4B5563]">Rm-nts</th><th className="px-2 text-[#4B5563]">Rate</th></tr></thead>
                          <tbody>{e.rows.map((r, i) => { const cols = Object.keys(Object.assign({}, ...e.rows.map((x) => x.quantities))).sort(); const rowRn = Object.values(r.quantities || {}).reduce((a, q) => a + (Number(q) || 0), 0); return (<tr key={i}><td className="pr-3 py-0.5 font-semibold text-[#111827]">{r.room_type}</td>{cols.map((d) => <td key={d} className="px-2 py-0.5 text-center">{r.quantities?.[d] || "·"}</td>)}<td className="px-2 py-0.5 text-center font-semibold text-[#002FA7]">{rowRn}</td><td className="px-2 py-0.5 text-center">{money(r.rate)}</td></tr>); })}</tbody>
                        </table>
                      </div>
                    )}
                    {e.notes && <div className="text-xs text-[#6B7280] mt-1 italic">“{e.notes}”</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-display font-black text-[#111827]">{money(e.revenue)}</span>
                  <button data-testid={`space-edit-${e.id}`} onClick={() => openEdit(e)} className="text-[#4B5563] hover:text-[#002FA7]"><PencilSimple size={16} weight="bold" /></button>
                  <button data-testid={`space-delete-${e.id}`} onClick={() => remove.mutate(e.id)} className="text-[#4B5563] hover:text-[#DC2626]"><Trash size={16} weight="bold" /></button>
                </div>
              </div>
              {(e.history || []).length > 0 && (
                <div className="mt-2 pl-9">
                  <button data-testid={`space-history-toggle-${e.id}`} onClick={() => setShowHistory(showHistory === e.id ? null : e.id)} className="inline-flex items-center gap-1 text-xs text-[#4B5563] hover:text-[#002FA7]"><ClockCounterClockwise size={13} weight="bold" /> {showHistory === e.id ? "Hide" : "Change log"} ({e.history.length})</button>
                  {showHistory === e.id && <div className="mt-1 space-y-1" data-testid={`space-history-${e.id}`}>{e.history.map((h, i) => <div key={i} className="text-[11px] text-[#6B7280]"><b className="text-[#111827]">{h.by}</b> {h.action} · {h.at?.slice(0, 10)} — {h.detail}</div>)}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-[#111827] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? `Edit ${KIND_META[editKind]?.label.toLowerCase()}` : "Add rooms, space & F&B"}</DialogTitle></DialogHeader>

          {!editId && <p className="text-sm text-[#4B5563] -mt-2">Fill in any of the tabs below — your entries are kept as you switch tabs and are all saved together on submit.</p>}

          <div className="flex gap-2" data-testid="space-type-toggle">
            {["agreement", "actual"].map((t) => (
              <button key={t} data-testid={`space-type-${t}`} onClick={() => setEntryType(t)} className={`flex-1 border px-3 py-2 text-sm font-semibold capitalize ${entryType === t ? (t === "agreement" ? "border-[#002FA7] bg-[#E0E7FF] text-[#002FA7]" : "border-[#047857] bg-[#D1FAE5] text-[#047857]") : "border-[#E5E7EB] text-[#4B5563] hover:border-[#111827]"}`}>{t === "agreement" ? "Agreement (contracted)" : "Actual (booked)"}</button>
            ))}
          </div>

          <div className="flex gap-2" data-testid="space-kind-toggle">
            {tabs.map((k) => {
              const m = KIND_META[k];
              return (
                <button key={k} data-testid={`space-kind-${k}`} onClick={() => setActiveTab(k)} className={`flex-1 flex items-center justify-center gap-1.5 border px-2 py-2 text-sm font-semibold relative ${activeTab === k ? "border-[#111827] bg-[#111827] text-white" : "border-[#E5E7EB] text-[#4B5563] hover:border-[#111827]"}`}>
                  <m.Icon size={15} weight="bold" /> {m.label.split(" ")[0]}
                  {!editId && sections[k] && <span data-testid={`space-kind-dot-${k}`} className={`absolute top-1 right-1 w-2 h-2 rounded-full ${activeTab === k ? "bg-white" : "bg-[#16A34A]"}`} />}
                </button>
              );
            })}
          </div>

          {activeTab === "rooms" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Arrival"><input data-testid="space-start-date" type="date" value={roomDates.start_date || ""} onChange={(e) => setRoomDates((s) => ({ ...s, start_date: e.target.value }))} className={inputCls} /></Field>
                <Field label="Departure"><input data-testid="space-end-date" type="date" value={roomDates.end_date || ""} onChange={(e) => setRoomDates((s) => ({ ...s, end_date: e.target.value }))} className={inputCls} /></Field>
              </div>
              {roomTypes.length === 0 ? (
                <p className="text-sm text-[#B45309]">No room types set for this property. A manager can add them under <b>Edit listing → Room types</b>.</p>
              ) : !roomDates.start_date ? (
                <p className="text-sm text-[#4B5563]">Pick an arrival date to enter rooms per night.</p>
              ) : (
                <div className="overflow-x-auto border border-[#E5E7EB]" data-testid="rooms-grid">
                  <table className="text-sm border-collapse min-w-full">
                    <thead><tr className="bg-[#F3F4F6]">
                      <th className="text-left px-2 py-2 text-xs font-semibold text-[#111827] sticky left-0 bg-[#F3F4F6]" style={{ minWidth: 160 }}>Room type</th>
                      <th className="px-2 py-2 text-xs font-semibold text-[#4B5563]" style={{ minWidth: 80 }}>Rate</th>
                      {nights.map((d) => <th key={d} className="px-1 py-2 text-xs font-semibold text-[#4B5563]" style={{ minWidth: 66 }}>{fmtDay(d)}</th>)}
                    </tr></thead>
                    <tbody>
                      {roomTypes.map((rt) => (
                        <tr key={rt.id} className="border-t border-[#E5E7EB]" data-testid={`grid-row-${rt.id}`}>
                          <td className="px-2 py-1 font-semibold text-[#111827] sticky left-0 bg-white" style={{ minWidth: 160 }}>{rt.name}<span className="text-[#9CA3AF] text-xs font-normal"> ({rt.inventory})</span></td>
                          <td className="px-1 py-1"><input data-testid={`grid-rate-${rt.id}`} type="number" value={rates[rt.name] ?? ""} onChange={(e) => setRates((r) => ({ ...r, [rt.name]: e.target.value }))} placeholder="$" className="w-16 border border-[#E5E7EB] px-1 py-1 text-sm" /></td>
                          {nights.map((d) => (
                            <td key={d} className="px-1 py-1"><input data-testid={`grid-qty-${rt.id}-${d}`} type="number" min="0" value={grid[rt.name]?.[d] ?? ""} onChange={(e) => setQty(rt.name, d, e.target.value)} className="w-14 border border-[#E5E7EB] px-1 py-1 text-sm text-center" /></td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === "meeting" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Meeting room">
                <select data-testid="mtg-room" value={mtg.room_id || ""} onChange={Sm("room_id")} className={`${inputCls} bg-white`}>
                  <option value="">— Select room —</option>
                  {meetingRooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </Field>
              <Field label="Function type">
                <select data-testid="mtg-type" value={mtg.function_type || ""} onChange={Sm("function_type")} className={`${inputCls} bg-white`}>
                  <option value="">— Select —</option>
                  {FUNCTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Date"><input data-testid="mtg-date" type="date" value={mtg.event_date || ""} onChange={Sm("event_date")} className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Start"><input data-testid="mtg-start" type="time" value={mtg.start_time || ""} onChange={Sm("start_time")} className={inputCls} /></Field>
                <Field label="End"><input data-testid="mtg-end" type="time" value={mtg.end_time || ""} onChange={Sm("end_time")} className={inputCls} /></Field>
              </div>
              <Field label="# Set for"><input data-testid="mtg-setfor" type="number" value={mtg.set_for || ""} onChange={Sm("set_for")} className={inputCls} /></Field>
              <Field label="Guarantee"><input data-testid="mtg-guarantee" type="number" value={mtg.guarantee || ""} onChange={Sm("guarantee")} className={inputCls} /></Field>
              <Field label="Setup"><input data-testid="mtg-setup" value={mtg.setup || ""} onChange={Sm("setup")} placeholder="Rounds / Theatre" className={inputCls} /></Field>
              <Field label="Room rental $"><input data-testid="mtg-rate" type="number" value={mtg.space_rate || ""} onChange={Sm("space_rate")} className={inputCls} /></Field>
              <Field label="F&B minimum $"><input data-testid="mtg-fnb" type="number" value={mtg.fnb_minimum || ""} onChange={Sm("fnb_minimum")} className={inputCls} /></Field>
              <Field label="F&B notes"><input data-testid="mtg-fnbnotes" value={mtg.fnb_notes || ""} onChange={Sm("fnb_notes")} className={inputCls} /></Field>
            </div>
          )}

          {activeTab === "fnb" && (
            <div className="grid grid-cols-1 gap-3">
              <Field label="F&B / catering minimum $"><input data-testid="space-fnb-min" type="number" value={fnb.fnb_minimum || ""} onChange={Sf("fnb_minimum")} className={inputCls} /></Field>
              <Field label="Notes"><input data-testid="space-fnb-notes" value={fnb.fnb_notes || ""} onChange={Sf("fnb_notes")} className={inputCls} /></Field>
            </div>
          )}

          <Field label="Notes (optional)"><textarea data-testid="space-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} /></Field>

          <DialogFooter>
            <div className="flex items-center justify-between w-full gap-4">
              {!editId ? (
                <span data-testid="space-save-summary" className="text-sm text-[#4B5563]">
                  {summaryParts.length ? <>You’re about to add: <b className="text-[#111827]">{summaryParts.join(" + ")}</b></> : "Nothing entered yet"}
                </span>
              ) : <span />}
              <button data-testid="space-save-btn" disabled={save.isPending} onClick={onSaveClick} className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] disabled:opacity-60 whitespace-nowrap">{save.isPending ? "Saving…" : (editId ? "Save changes" : summaryParts.length > 1 ? `Add ${summaryParts.length} items` : "Add")}</button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
